import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Button, Card, Input } from '@/components';
import { apiJson, ApiError } from '@/lib/api';

type AppVersionConfigItem = {
  platform: string;
  latest_version: string | null;
  soft_wall_version: string | null;
  hard_wall_version: string | null;
  updated_at: string;
};

type PlatformFormState = {
  latest_version: string;
  soft_wall_version: string;
  hard_wall_version: string;
};

const PLATFORM_LABELS: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
};

function emptyForm(): PlatformFormState {
  return {
    latest_version: '',
    soft_wall_version: '',
    hard_wall_version: '',
  };
}

function formFromItem(item: AppVersionConfigItem | undefined): PlatformFormState {
  if (!item) return emptyForm();
  return {
    latest_version: item.latest_version ?? '',
    soft_wall_version: item.soft_wall_version ?? '',
    hard_wall_version: item.hard_wall_version ?? '',
  };
}

function parseDetail(err: unknown): string {
  if (err instanceof ApiError && err.bodyText) {
    try {
      const j = JSON.parse(err.bodyText) as { detail?: string | string[] };
      if (typeof j.detail === 'string') return j.detail;
      if (Array.isArray(j.detail)) return j.detail.map(String).join('; ');
    } catch {
      /* ignore */
    }
    return err.bodyText;
  }
  return err instanceof Error ? err.message : 'Request failed';
}

function nullableField(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function formatAdminDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export const Route = createFileRoute('/admin/app-version')({
  component: AppVersionPage,
});

function AppVersionPage() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'app-version'],
    queryFn: () =>
      apiJson<AppVersionConfigItem[]>(getToken, '/v1/admin/app-version'),
  });

  if (isLoading) {
    return <div className="py-12 text-center text-sm text-gray-500">Loading…</div>;
  }

  if (error) {
    return (
      <p className="text-sm text-red-600">
        {parseDetail(error)}
        <button type="button" className="ml-2 underline" onClick={() => void refetch()}>
          Retry
        </button>
      </p>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">App version</h1>
        <p className="mt-1 text-sm text-gray-500">
          Per-platform thresholds returned by{' '}
          <code className="text-xs">GET /v1/app-version</code>. Leave a field blank to
          disable that tier.
        </p>
      </div>

      {(['ios', 'android'] as const).map((platform) => (
        <PlatformConfigCard
          key={platform}
          platform={platform}
          item={data?.find((row) => row.platform === platform)}
          onSaved={() => {
            void qc.invalidateQueries({ queryKey: ['admin', 'app-version'] });
          }}
        />
      ))}

      <Button variant="secondary" onClick={() => void refetch()}>
        Refresh
      </Button>
    </div>
  );
}

function PlatformConfigCard({
  platform,
  item,
  onSaved,
}: {
  platform: 'ios' | 'android';
  item: AppVersionConfigItem | undefined;
  onSaved: () => void;
}) {
  const { getToken } = useAuth();
  const [form, setForm] = useState<PlatformFormState>(() => formFromItem(item));
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setForm(formFromItem(item));
  }, [item]);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiJson<AppVersionConfigItem>(getToken, `/v1/admin/app-version/${platform}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          latest_version: nullableField(form.latest_version),
          soft_wall_version: nullableField(form.soft_wall_version),
          hard_wall_version: nullableField(form.hard_wall_version),
        }),
      }),
    onSuccess: () => {
      setSaveError(null);
      onSaved();
    },
    onError: (err) => {
      setSaveError(parseDetail(err));
    },
  });

  return (
    <Card className="p-6">
      <h2 className="mb-1 text-lg font-medium text-gray-900">
        {PLATFORM_LABELS[platform]}
      </h2>
      <p className="mb-4 text-sm text-gray-600">
        Hard wall blocks the app, soft wall nudges an update, latest marks the current store
        version.
      </p>

      <div className="space-y-4">
        <Input
          label="Latest version"
          placeholder="e.g. 4.2.0"
          value={form.latest_version}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, latest_version: e.target.value }))
          }
        />
        <Input
          label="Soft wall version"
          placeholder="e.g. 4.0.0"
          value={form.soft_wall_version}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, soft_wall_version: e.target.value }))
          }
        />
        <Input
          label="Hard wall version"
          placeholder="e.g. 3.5.0"
          value={form.hard_wall_version}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, hard_wall_version: e.target.value }))
          }
        />
      </div>

      {saveError ? <p className="mt-3 text-sm text-red-600">{saveError}</p> : null}

      <div className="mt-4 flex items-center gap-3">
        <Button
          onClick={() => void saveMutation.mutate()}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? 'Saving…' : 'Save'}
        </Button>
        {item?.updated_at ? (
          <p className="text-xs text-gray-500">
            Last updated {formatAdminDateTime(item.updated_at)}
          </p>
        ) : (
          <p className="text-xs text-gray-500">Not saved yet</p>
        )}
      </div>
    </Card>
  );
}
